import { apiClient } from '@/shared/api/client';

export interface CreateOfferRequest {
  title: string;
  description: string;
  address: string;
  latitude?: number;
  longitude?: number;
  startDate: string;
  endDate: string;
  products: Array<{
    materialId: string;
    materialCategory?: string;
    materialSubcategory?: string;
    unitPrice: number;
    quantity: number;
    unitId: string;
    status: 'NEW' | 'USED';
  }>;
}

export interface PhotoResponse {
  id: string;
  url: string;
  thumbnailUrl?: string;
  order: number;
  uploadedAt: string;
}

export interface DocumentResponse {
  id: string;
  url: string;
  filename: string;
  fileType: string;
  fileSize: number;
  order: number;
  uploadedAt: string;
}

export interface OfferResponse {
  id: string;
  userId: string;
  title: string;
  description: string;
  address: string;
  status: 'DRAFT' | 'PUBLISHED' | 'SOLD' | 'ARCHIVED';
  products: Array<{
    id: string;
    name: string;
    quantity: number;
    unit: {
      id: string;
      code: string;
      labelEn: string;
      labelFr: string;
    };
    status: 'NEW' | 'USED';
    photos: PhotoResponse[];
    createdAt: string;
    updatedAt: string;
  }>;
  documents: DocumentResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateOffersResponse {
  offers: OfferResponse[];
}

export const offerService = {
  async createOffer(
    data: CreateOfferRequest,
    photos: File[] = [],
    documents: File[] = [],
    photoCounts: number[] = []
  ): Promise<CreateOffersResponse> {
    // Build FormData for multipart upload
    const formData = new FormData();

    // Append offer data as JSON string
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('address', data.address);

    // Append coordinates if available
    if (data.latitude !== undefined) {
      formData.append('latitude', data.latitude.toString());
    }
    if (data.longitude !== undefined) {
      formData.append('longitude', data.longitude.toString());
    }

    formData.append('startDate', data.startDate);
    formData.append('endDate', data.endDate);

    // Append products as nested JSON (NestJS will parse this)
    data.products.forEach((product, index) => {
      formData.append(`products[${index}][materialId]`, product.materialId);
      if (product.materialCategory) {
        formData.append(`products[${index}][materialCategory]`, product.materialCategory);
      }
      if (product.materialSubcategory) {
        formData.append(`products[${index}][materialSubcategory]`, product.materialSubcategory);
      }
      formData.append(`products[${index}][unitPrice]`, product.unitPrice.toString());
      formData.append(`products[${index}][quantity]`, product.quantity.toString());
      formData.append(`products[${index}][unitId]`, product.unitId);
      formData.append(`products[${index}][status]`, product.status);
    });

    photoCounts.forEach((count, index) => {
      formData.append(`photoCounts[${index}]`, count.toString());
    });

    // Append photos
    photos.forEach(photo => {
      formData.append('photos', photo);
    });

    // Append documents
    documents.forEach(document => {
      formData.append('documents', document);
    });

    return apiClient.post<CreateOffersResponse>('/offers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  async getUserOffers(): Promise<OfferResponse[]> {
    return apiClient.get<OfferResponse[]>('/offers/me');
  },

  async getOfferById(id: string): Promise<OfferResponse> {
    return apiClient.get<OfferResponse>(`/offers/${id}`);
  },

  async updateOffer(
    id: string,
    data: Partial<Omit<CreateOfferRequest, 'products'>> & {
      materialId?: string;
      materialCategory?: string;
      materialSubcategory?: string;
      unitPrice?: number;
      quantity?: number;
      unitId?: string;
      productStatus?: 'NEW' | 'USED';
    },
    newPhotos: File[] = [],
    newDocuments: File[] = [],
    keepPhotoIds?: string[],
    keepDocumentIds?: string[]
  ): Promise<OfferResponse> {
    const formData = new FormData();

    if (data.title !== undefined) formData.append('title', data.title);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.address !== undefined) formData.append('address', data.address);
    if (data.latitude != null) formData.append('latitude', data.latitude.toString());
    if (data.longitude != null) formData.append('longitude', data.longitude.toString());
    if (data.startDate !== undefined) formData.append('startDate', data.startDate);
    if (data.endDate !== undefined) formData.append('endDate', data.endDate);

    if (data.materialId !== undefined) formData.append('materialId', data.materialId);
    if (data.materialCategory !== undefined)
      formData.append('materialCategory', data.materialCategory);
    if (data.materialSubcategory !== undefined)
      formData.append('materialSubcategory', data.materialSubcategory);
    if (data.unitPrice !== undefined) formData.append('unitPrice', data.unitPrice.toString());
    if (data.quantity !== undefined) formData.append('quantity', data.quantity.toString());
    if (data.unitId !== undefined) formData.append('unitId', data.unitId);
    if (data.productStatus !== undefined) formData.append('productStatus', data.productStatus);

    if (keepPhotoIds !== undefined) {
      formData.append('keepPhotoIds', JSON.stringify(keepPhotoIds));
    }
    if (keepDocumentIds !== undefined) {
      formData.append('keepDocumentIds', JSON.stringify(keepDocumentIds));
    }

    newPhotos.forEach(photo => formData.append('photos', photo));
    newDocuments.forEach(doc => formData.append('documents', doc));

    return apiClient.post<OfferResponse>(`/offers/${id}/update`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
